import { doc, setDoc } from 'firebase/firestore';
import firebaseFirestore from '~/config/firebase/firestore';
import { SalaInforme, Tarifa } from './ocupacion';

/**
 * Guardar la tarifa de varias salas desde el simulador: lo mismo que hace
 * Ajustes → la sede → Bloques → Tarifa, para no tener que salir del informe.
 *
 * Escribe SOLO `schedule.settings.hourlyPrice` y `multiHourDiscountPercent`
 * con `merge`, así el resto de la configuración de la sede no se toca, y deja
 * en `activity_logs` la misma huella que Ajustes (un registro por campo que
 * cambia, con el valor anterior y el nuevo).
 */

export type CambioTarifa = { id: string; precioHora: number; descuento: number };

type Registrar = (actividad: {
  activityType: 'settings_update';
  entityId: string;
  entityType: 'location';
  details: Record<string, unknown>;
}) => Promise<unknown>;

export const tarifaDeSala = (s: SalaInforme): Tarifa => ({ precioHora: s.precioHora, descuento: s.descuento });

/** Los cambios que de verdad cambian algo respecto a lo guardado. */
export const cambiosReales = (salas: SalaInforme[], cambios: CambioTarifa[]): CambioTarifa[] =>
  cambios.filter((c) => {
    const s = salas.find((x) => x.id === c.id);
    if (!s) return false;
    const precioActual = s.precioHora ?? 0;
    const descuentoActual = Math.round(s.descuento * 100);
    return precioActual !== c.precioHora || descuentoActual !== Math.round(c.descuento * 100);
  });

export const guardarTarifas = async (
  salas: SalaInforme[],
  cambios: CambioTarifa[],
  registrar: Registrar,
  usuario: string
): Promise<void> => {
  for (const c of cambiosReales(salas, cambios)) {
    const sala = salas.find((x) => x.id === c.id)!;
    const descuentoPct = Math.round(c.descuento * 100);
    await setDoc(
      doc(firebaseFirestore, 'locations', c.id),
      { schedule: { settings: { hourlyPrice: c.precioHora, multiHourDiscountPercent: descuentoPct } } },
      { merge: true }
    );
    const registros: { setting: string; displayName: string; previousValue: number; newValue: number }[] = [];
    if ((sala.precioHora ?? 0) !== c.precioHora) {
      registros.push({
        setting: 'hourlyPrice',
        displayName: 'Precio por hora',
        previousValue: sala.precioHora ?? 0,
        newValue: c.precioHora,
      });
    }
    if (Math.round(sala.descuento * 100) !== descuentoPct) {
      registros.push({
        setting: 'multiHourDiscountPercent',
        displayName: 'Descuento por más de 1 hora (%)',
        previousValue: Math.round(sala.descuento * 100),
        newValue: descuentoPct,
      });
    }
    for (const r of registros) {
      await registrar({
        activityType: 'settings_update',
        entityId: c.id,
        entityType: 'location',
        details: {
          setting: r.setting,
          settingDisplayName: r.displayName,
          previousValue: r.previousValue,
          newValue: r.newValue,
          locationName: sala.nombre,
          updatedBy: usuario,
          // Para distinguirlo en el historial de lo que se cambia en Ajustes.
          from: 'informe-ocupacion',
        },
      });
    }
  }
};
